export enum InvestmentManagementAccountType {
    IRA = 'IRA',
    ROTH_IRA = 'ROTH_IRA',
    SEP_IRA = 'SEP_IRA',
    SIMPLE_IRA = 'SIMPLE_IRA',
    INHERITED_IRA = 'INHERITED_IRA',
    INDIVIDUAL_BROKERAGE = 'INDIVIDUAL_BROKERAGE',
    JOINT_BROKERAGE = 'JOINT_BROKERAGE',
    TRUST_BROKERAGE = 'TRUST_BROKERAGE',
    CUSTODIAL_ROTH = 'CUSTODIAL_ROTH',
    UGMA = 'UGMA',
    UTMA = 'UTMA',
}

export const ReadableInvestmentManagementAccountType: Record<
    InvestmentManagementAccountType,
    string
> = {
    [InvestmentManagementAccountType.IRA]: 'IRA',
    [InvestmentManagementAccountType.ROTH_IRA]: 'Roth IRA',
    [InvestmentManagementAccountType.SEP_IRA]: 'SEP IRA',
    [InvestmentManagementAccountType.SIMPLE_IRA]: 'Simple IRA',
    [InvestmentManagementAccountType.INHERITED_IRA]: 'Inherited IRA',
    [InvestmentManagementAccountType.INDIVIDUAL_BROKERAGE]:
        'Individual brokerage',
    [InvestmentManagementAccountType.JOINT_BROKERAGE]: 'Joint brokerage',
    [InvestmentManagementAccountType.TRUST_BROKERAGE]: 'Trust brokerage',
    [InvestmentManagementAccountType.CUSTODIAL_ROTH]: 'Custodial Roth',
    [InvestmentManagementAccountType.UGMA]: 'UGMA',
    [InvestmentManagementAccountType.UTMA]: 'UTMA',
};

export enum Portfolio {
    CORE = 'CORE',
    ESG = 'ESG',
}

export enum RiskLevel {
    ONE = '1',
    TWO = '2',
    THREE = '3',
    FOUR = '4',
    FIVE = '5',
    SIX = '6',
    SEVEN = '7',
    EIGHT = '8',
    NINE = '9',
    TEN = '10',
}

export const MAX_RISK_LEVEL = RiskLevel.TEN;

export enum TimeHorizon {
    SHORT = 'SHORT',
    AVERAGE = 'AVERAGE',
    LONG = 'LONG',
}

/**
 * Ascend enrollment type strings stored in `apex_accounts.registration_type` (Ascend
 * `EnrollmentCreateType`). Keep aligned with fruitful-backend `apexAccountDto` and the Ascend SDK.
 */
export enum SupportedApexAccountRegistrationType {
    RegistrationIndividual = 'REGISTRATION_INDIVIDUAL',
    RegistrationIraTraditional = 'REGISTRATION_IRA_TRADITIONAL',
    RegistrationIraRoth = 'REGISTRATION_IRA_ROTH',
    RegistrationIraSimple = 'REGISTRATION_IRA_SIMPLE',
    RegistrationCustodialIraRoth = 'REGISTRATION_CUSTODIAL_IRA_ROTH',
    RegistrationIraSep = 'REGISTRATION_IRA_SEP',
    RegistrationIraRollover = 'REGISTRATION_IRA_ROLLOVER',
    RegistrationIraBeneficiaryTraditional = 'REGISTRATION_IRA_BENEFICIARY_TRADITIONAL',
    RegistrationIraBeneficiaryRoth = 'REGISTRATION_IRA_BENEFICIARY_ROTH',
    RegistrationCustodial = 'REGISTRATION_CUSTODIAL',
    RegistrationTrust = 'REGISTRATION_TRUST',
    RegistrationJointWros = 'REGISTRATION_JOINT_WROS',
}

export const ReadableApexAccountRegistrationType: Record<
    SupportedApexAccountRegistrationType,
    string
> = {
    [SupportedApexAccountRegistrationType.RegistrationIndividual]:
        'Individual brokerage',
    [SupportedApexAccountRegistrationType.RegistrationIraTraditional]: 'IRA',
    [SupportedApexAccountRegistrationType.RegistrationIraRoth]: 'Roth IRA',
    [SupportedApexAccountRegistrationType.RegistrationIraSimple]: 'SIMPLE IRA',
    [SupportedApexAccountRegistrationType.RegistrationCustodialIraRoth]:
        'Custodial Roth IRA',
    [SupportedApexAccountRegistrationType.RegistrationIraSep]: 'SEP IRA',
    [SupportedApexAccountRegistrationType.RegistrationIraRollover]:
        'Rollover IRA',
    [SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryTraditional]:
        'Inherited IRA',
    [SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryRoth]:
        'Inherited Roth IRA',
    [SupportedApexAccountRegistrationType.RegistrationCustodial]: 'UTMA',
    [SupportedApexAccountRegistrationType.RegistrationTrust]: 'Trust brokerage',
    [SupportedApexAccountRegistrationType.RegistrationJointWros]:
        'Joint brokerage',
};

const NON_RETIREMENT_REGISTRATION_TYPES: string[] = [
    SupportedApexAccountRegistrationType.RegistrationIndividual,
    SupportedApexAccountRegistrationType.RegistrationJointWros,
    SupportedApexAccountRegistrationType.RegistrationCustodial,
    SupportedApexAccountRegistrationType.RegistrationTrust,
];

export function isRetirementRegistrationType(
    registrationType: string | null | undefined,
): boolean {
    if (registrationType == null) return false;
    return !NON_RETIREMENT_REGISTRATION_TYPES.includes(registrationType);
}

/** Membership-style ownership for display. UTMA / trust are not Individual. */
export type ApexRegistrationOwnershipLabel =
    | 'Individual'
    | 'Joint'
    | 'Custodial'
    | 'Trust';

export const getApexRegistrationOwnershipLabel = (
    registrationType: string | null | undefined,
): ApexRegistrationOwnershipLabel | null => {
    if (registrationType == null || registrationType === '') {
        return null;
    }

    switch (registrationType) {
        case SupportedApexAccountRegistrationType.RegistrationJointWros:
            return 'Joint';
        case SupportedApexAccountRegistrationType.RegistrationCustodial:
        case SupportedApexAccountRegistrationType.RegistrationCustodialIraRoth:
            return 'Custodial';
        case SupportedApexAccountRegistrationType.RegistrationTrust:
            return 'Trust';
        case SupportedApexAccountRegistrationType.RegistrationIndividual:
        case SupportedApexAccountRegistrationType.RegistrationIraTraditional:
        case SupportedApexAccountRegistrationType.RegistrationIraRoth:
        case SupportedApexAccountRegistrationType.RegistrationIraSimple:
        case SupportedApexAccountRegistrationType.RegistrationIraSep:
        case SupportedApexAccountRegistrationType.RegistrationIraRollover:
        case SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryTraditional:
        case SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryRoth:
            return 'Individual';
        default:
            return null;
    }
};

export const getLegacyImAccountOwnershipLabel = (
    accountType: InvestmentManagementAccountType | null | undefined,
): ApexRegistrationOwnershipLabel | null => {
    if (accountType == null) {
        return null;
    }

    switch (accountType) {
        case InvestmentManagementAccountType.JOINT_BROKERAGE:
            return 'Joint';
        case InvestmentManagementAccountType.UTMA:
        case InvestmentManagementAccountType.UGMA:
        case InvestmentManagementAccountType.CUSTODIAL_ROTH:
            return 'Custodial';
        case InvestmentManagementAccountType.TRUST_BROKERAGE:
            return 'Trust';
        default:
            return 'Individual';
    }
};

/**
 * Not openable via money map / go flow (`CreateApexAccountDto`). Matches backend
 * `GO_FLOW_EXCLUDED_ENROLLMENT_TYPES`.
 */
export const GO_FLOW_EXCLUDED_APEX_ENROLLMENT_TYPES = [
    SupportedApexAccountRegistrationType.RegistrationIraSimple,
    SupportedApexAccountRegistrationType.RegistrationCustodialIraRoth,
    SupportedApexAccountRegistrationType.RegistrationCustodial,
    SupportedApexAccountRegistrationType.RegistrationTrust,
    SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryTraditional,
    SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryRoth,
] as const;

export type GoFlowOpenableApexEnrollmentType = Exclude<
    SupportedApexAccountRegistrationType,
    (typeof GO_FLOW_EXCLUDED_APEX_ENROLLMENT_TYPES)[number]
>;

const goFlowExcludedApexEnrollmentSet = new Set<string>(
    GO_FLOW_EXCLUDED_APEX_ENROLLMENT_TYPES,
);

/** Supported enrollment types allowed for go flow / `CreateApexAccountDto`. Matches backend `GO_FLOW_OPENABLE_ENROLLMENT_TYPE_VALUES`. */
export const GO_FLOW_OPENABLE_APEX_ENROLLMENT_TYPES: GoFlowOpenableApexEnrollmentType[] =
    (
        Object.values(
            SupportedApexAccountRegistrationType,
        ) as SupportedApexAccountRegistrationType[]
    ).filter(
        (t): t is GoFlowOpenableApexEnrollmentType =>
            !goFlowExcludedApexEnrollmentSet.has(t),
    );

export enum ApexAccountState {
    OPEN = 'OPEN',
    CLOSED = 'CLOSED',
    PENDING = 'PENDING',
    STATE_UNSPECIFIED = 'STATE_UNSPECIFIED',
}

export enum ApexDerivedSubState {
    /** Joint account created; primary owner has saved agreements. Awaiting second party acceptance. */
    PENDING_PARTNER_ACCEPT = 'PENDING_PARTNER_ACCEPT',
    /** Account enrolled; investigation in progress. */
    PENDING_INVESTIGATION_RUN = 'PENDING_INVESTIGATION_RUN',
    /** Investigation or watchlist result was not PASSED; ops review required. */
    PENDING_INVESTIGATION_MANUAL_REVIEW = 'PENDING_INVESTIGATION_MANUAL_REVIEW',
    /** Open account, no active restrictions. */
    OPEN_ACTIVE = 'OPEN_ACTIVE',
    /** Open account with one or more active restrictions from {@link RESTRICTIVE_CODES}. */
    OPEN_RESTRICTED = 'OPEN_RESTRICTED',
    /** Open account with a closure-related restriction from {@link CLOSURE_CODES}; closure in progress. */
    OPEN_IN_CLOSING = 'OPEN_IN_CLOSING',
    /** Account closed. */
    CLOSED = 'CLOSED',
}

// Enum for categorizing account types into sections for UI grouping
// This grouping needs to be 1-to-1 with Apex disclosure documents
export enum ApexAccountSectionType {
    INDIVIDUAL_BROKERAGE = 'INDIVIDUAL_BROKERAGE',
    RETIREMENT = 'RETIREMENT',
    JOINT_BROKERAGE = 'JOINT_BROKERAGE',
}

// Mapping from SupportedApexAccountRegistrationType to section type
export const apexAccountTypeToSection: Record<
    SupportedApexAccountRegistrationType,
    ApexAccountSectionType
> = {
    [SupportedApexAccountRegistrationType.RegistrationIndividual]:
        ApexAccountSectionType.INDIVIDUAL_BROKERAGE,
    [SupportedApexAccountRegistrationType.RegistrationIraTraditional]:
        ApexAccountSectionType.RETIREMENT,
    [SupportedApexAccountRegistrationType.RegistrationIraRoth]:
        ApexAccountSectionType.RETIREMENT,
    [SupportedApexAccountRegistrationType.RegistrationIraSimple]:
        ApexAccountSectionType.RETIREMENT,
    [SupportedApexAccountRegistrationType.RegistrationCustodialIraRoth]:
        ApexAccountSectionType.RETIREMENT,
    [SupportedApexAccountRegistrationType.RegistrationIraSep]:
        ApexAccountSectionType.RETIREMENT,
    [SupportedApexAccountRegistrationType.RegistrationIraRollover]:
        ApexAccountSectionType.RETIREMENT,
    [SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryTraditional]:
        ApexAccountSectionType.RETIREMENT,
    [SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryRoth]:
        ApexAccountSectionType.RETIREMENT,
    [SupportedApexAccountRegistrationType.RegistrationCustodial]:
        ApexAccountSectionType.INDIVIDUAL_BROKERAGE,
    [SupportedApexAccountRegistrationType.RegistrationTrust]:
        ApexAccountSectionType.INDIVIDUAL_BROKERAGE,
    [SupportedApexAccountRegistrationType.RegistrationJointWros]:
        ApexAccountSectionType.JOINT_BROKERAGE,
};

export enum RetirementContributionType {
    TYPE_UNSPECIFIED = 'TYPE_UNSPECIFIED',
    REGULAR = 'REGULAR',
    EMPLOYEE = 'EMPLOYEE',
    EMPLOYER = 'EMPLOYER',
    RECHARACTERIZATION = 'RECHARACTERIZATION',
    ROLLOVER_60_DAY = 'ROLLOVER_60_DAY',
    ROLLOVER_DIRECT = 'ROLLOVER_DIRECT',
    TRANSFER = 'TRANSFER',
    TRUSTEE_FEE = 'TRUSTEE_FEE',
    CONVERSION = 'CONVERSION',
    REPAYMENT = 'REPAYMENT',
    CONTRIBUTION_NON_REPORTABLE = 'CONTRIBUTION_NON_REPORTABLE',
}
